import { isNativeApp } from '@/lib/platform';

/**
 * =============================================================================
 *  工作階段 token 的保管處（只有 iOS App 用得到）。
 *
 *  網站的工作階段是 HttpOnly cookie，JS 碰不到也不該碰到：這裡在網站上全是 no-op，
 *  `getToken()` 恆為 null，請求照舊靠瀏覽器自己帶 cookie。
 *
 *  iOS App 對 API 是跨來源（`capacitor://localhost` → `https://….ts.net`），cookie 用不了，
 *  所以後端把 token 放在回應 body（`session_token`）。它存進 **Keychain**——經由 App 自己的
 *  Swift Capacitor plugin `AuthTokenStore`（mobile/ios-shell），不是 localStorage：
 *  WKWebView 的 localStorage 是一個任何注入的腳本都讀得到、也會被系統清掉的地方。
 *
 *  Capacitor 在頁面腳本之前把已註冊的原生 plugin 掛到 `window.Capacitor.Plugins`，
 *  所以這裡不需要 `@capacitor/core`，只在本檔宣告用到的那一小塊形狀。
 *
 *  Keychain 每讀一次都要走一趟原生橋；token 讀過一次就留在記憶體（`cached`），
 *  寫入與清除同時更新記憶體與 Keychain。
 * ========================================================================== */

interface AuthTokenStorePlugin {
  get(): Promise<{ value?: string | null }>;
  set(options: { value: string }): Promise<void>;
  clear(): Promise<void>;
}

interface CapacitorPlugins {
  Capacitor?: { Plugins?: { AuthTokenStore?: AuthTokenStorePlugin } };
}

function nativeStore(): AuthTokenStorePlugin | null {
  if (!isNativeApp()) return null;
  return (window as unknown as CapacitorPlugins).Capacitor?.Plugins?.AuthTokenStore ?? null;
}

/** null＝還沒讀過 Keychain；讀過之後是那一次（或之後 set/clear）的結果。 */
let cached: Promise<string | null> | null = null;

export function getToken(): Promise<string | null> {
  const store = nativeStore();
  if (store === null) return Promise.resolve(null);
  cached ??= store.get().then(
    (result) => (typeof result.value === 'string' && result.value !== '' ? result.value : null),
    (error: unknown) => {
      // 讀不到就當作沒登入，但下一個請求再試一次——不要把一次橋接失敗記成永久登出。
      console.warn('auth_token_read_failed', error);
      cached = null;
      return null;
    },
  );
  return cached;
}

/**
 * 登入／註冊／TOTP 第二步成功後存下 `session_token`。
 *
 * Keychain 寫不進去時**這一次開啟的 App 仍然是登入的**（記憶體裡有），只是下次開啟要重新
 * 登入——比讓一個伺服器端已經成功的登入在畫面上顯示失敗好。失敗會留一行 console.error。
 */
export async function setToken(token: string): Promise<void> {
  const store = nativeStore();
  if (store === null) return;
  cached = Promise.resolve(token);
  try {
    await store.set({ value: token });
  } catch (error) {
    console.error('auth_token_persist_failed', error);
  }
}

/** 登出、重設密碼、工作階段失效：記憶體與 Keychain 一起清掉。 */
export async function clearToken(): Promise<void> {
  pendingMfaToken = null;
  const store = nativeStore();
  if (store === null) return;
  cached = Promise.resolve(null);
  try {
    await store.clear();
  } catch (error) {
    console.error('auth_token_clear_failed', error);
  }
}

/**
 * 帳號開了兩步驟驗證時，App 的 `/auth/login` 回的是一張**五分鐘**的挑戰 token
 * （`mfa_token`），送回 `/auth/totp/verify` 的 `X-Mfa-Token`。它只活在記憶體裡：
 * 五分鐘的東西不值得進 Keychain，App 被關掉就重新登入。網站上它恆為 null（挑戰在 cookie 裡）。
 */
let pendingMfaToken: string | null = null;

export function setPendingMfaToken(token: string | null): void {
  pendingMfaToken = token;
}

export function getPendingMfaToken(): string | null {
  return pendingMfaToken;
}
